"use client";

import { TabData } from "@/types";
import { useDataTabsState, TabValue } from "@/hooks/useDataTabsState";
import DataTabsHeader from "./tabs/DataTabsHeader";
import DataTabsStatCards from "./tabs/DataTabsStatCards";
import DataTabsToolbar from "./tabs/DataTabsToolbar";
import DataTabsTabBar from "./tabs/DataTabsTabBar";
import PatternsTab from "./tabs/PatternsTab";
import DojiTab from "./tabs/DojiTab";
import StructureTab from "./tabs/StructureTab";
import ReversalTab from "./tabs/ReversalTab";
import DivergencesTab from "./tabs/DivergencesTab";
import TrendTab from "./tabs/TrendTab";
import { formatWeekRange, formatMonthRange } from "./tabs/utils";

interface DataTabsProps {
  onDataLoaded?: (data: TabData) => void;
  initialTab?: TabValue;
  showTabBar?: boolean;
}

export default function DataTabs({ onDataLoaded, initialTab = "patterns", showTabBar = true }: DataTabsProps) {
  const state = useDataTabsState({ onDataLoaded, initialTab });

  const {
    activeTab, setActiveTab,
    timeframe, setTimeframe,
    symbolQuery, setSymbolQuery,
    trendFilter, setTrendFilter,
    divergenceFilter, setDivergenceFilter,
    divergenceClassFilter, setDivergenceClassFilter,
    selectedCandleType, setSelectedCandleType,
    selectedDojiType, setSelectedDojiType,
    selectedStructureType, setSelectedStructureType,
    selectedReversalType, setSelectedReversalType,
    selectedPatternForKnowledge, setSelectedPatternForKnowledge,
    copied, handleShare,
    loading, error, candleViews,
    candleItems, dojiItems, structureItemsRaw, reversalItemsRaw,
    structureItems, reversalItems, divergencesBase,
    patternSetupGroups, dojiSetupGroups, structureSetupGroups, reversalSetupGroups,
    uniqueCandleTypes, uniqueDojiTypes, uniqueStructureTypes, uniqueReversalTypes,
    latestDataDate, latestDataLabel,
    activeItemCount, activeTypeCount, topFocusLine,
    activeMeta,
  } = state;

  const normalizedSymbolQuery = symbolQuery.trim().toUpperCase();

  const tabCounts = {
    patterns: candleItems.length,
    doji: dojiItems.length,
    structure: structureItems.length,
    reversal: reversalItems.length,
    divergences: divergencesBase.length,
    trend: "-" as unknown as number,
  };

  return (
    <section className="data-tabs-section">
      <div style={{ maxWidth: "1680px", margin: "0 auto", padding: "2rem 1rem" }}>

        <DataTabsHeader
          eyebrow={activeMeta.eyebrow}
          title={activeMeta.title}
          subtitle={activeMeta.subtitle}
          topFocusLine={topFocusLine}
          copied={copied}
          onShare={handleShare}
        />

        <DataTabsStatCards
          activeItemCount={activeItemCount}
          activeTypeCount={activeTypeCount}
          timeframe={timeframe}
          latestDataLabel={latestDataLabel}
          activeTab={activeTab}
          normalizedSymbolQuery={normalizedSymbolQuery}
          candleViewsPatternsHits={candleViews?.patterns?.summary.setup_hits}
        />

        {["patterns", "doji", "structure", "reversal"].includes(activeTab) && (
          <div style={{ background: "rgba(56, 189, 248, 0.08)", border: "1px solid rgba(56, 189, 248, 0.3)", borderRadius: "0.75rem", padding: "0.85rem 1rem", marginBottom: "1.5rem", color: "rgba(226, 232, 240, 0.9)", fontSize: "0.95rem" }}>
            Trend definition: <strong>Uptrend</strong> when EMA50 &gt; EMA200,{" "}
            <strong>Downtrend</strong> when EMA50 &lt; EMA200, otherwise <strong>Range</strong>. Requires at least 200 bars.
          </div>
        )}

        <DataTabsToolbar
          timeframe={timeframe}
          onTimeframeChange={setTimeframe}
          symbolQuery={symbolQuery}
          onSymbolQueryChange={setSymbolQuery}
          hideMonthly={activeTab === "divergences"}
        />

        {showTabBar && (
          <DataTabsTabBar activeTab={activeTab} onTabChange={setActiveTab} counts={tabCounts} />
        )}

        {/* Loading */}
        {loading && (
          <div style={{ padding: "3rem 2rem", textAlign: "center", background: "var(--color-bg-secondary)", borderRadius: "var(--radius)" }}>
            <div style={{ display: "inline-block", width: "40px", height: "40px", border: "4px solid rgba(59, 130, 246, 0.2)", borderTop: "4px solid var(--blue-text)", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
            <p style={{ color: "var(--color-text-muted)", marginTop: "1rem" }}>Loading data...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div style={{ padding: "2rem", background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.5)", borderRadius: "var(--radius)", color: "#fca5a5" }}>
            <p>{error}</p>
          </div>
        )}

        {/* Content */}
        {!loading && !error && (
          <>
            {["patterns", "doji", "structure", "reversal"].includes(activeTab) && (
              <div style={{ background: "rgba(59, 130, 246, 0.1)", border: "1px solid rgba(59, 130, 246, 0.3)", borderRadius: "var(--radius)", padding: "1rem 1.5rem", marginBottom: "2rem" }}>
                <p style={{ color: "rgba(241, 245, 249, 0.8)", fontSize: "0.95rem", margin: 0 }}>
                  <strong>Data Last Updated:</strong> {latestDataDate}
                </p>
                <div style={{ marginTop: "1rem", padding: "1rem", background: "rgba(15, 23, 42, 0.8)", borderRadius: "0.5rem", border: "1px solid rgba(148, 163, 184, 0.3)" }}>
                  {timeframe === "weekly" && (
                    <div>
                      <p style={{ color: "var(--color-text)", fontSize: "1rem", fontWeight: "600", margin: "0 0 0.5rem 0" }}>Weekly Candle</p>
                      <p style={{ color: "rgba(241, 245, 249, 0.8)", fontSize: "0.95rem", margin: 0 }}>Week of <strong>{formatWeekRange(new Date().toISOString())}</strong></p>
                      <p style={{ color: "rgba(241, 245, 249, 0.6)", fontSize: "0.85rem", margin: 0 }}>Last completed trading week (Monday–Friday)</p>
                    </div>
                  )}
                  {timeframe === "monthly" && (
                    <div>
                      <p style={{ color: "var(--color-text)", fontSize: "1rem", fontWeight: "600", margin: "0 0 0.5rem 0" }}>Monthly Candle</p>
                      <p style={{ color: "rgba(241, 245, 249, 0.8)", fontSize: "0.95rem", margin: 0 }}><strong>{formatMonthRange(new Date().toISOString())}</strong></p>
                      <p style={{ color: "rgba(241, 245, 249, 0.6)", fontSize: "0.85rem", margin: 0 }}>Last completed calendar month</p>
                    </div>
                  )}
                  {timeframe === "daily" && (
                    <p style={{ color: "rgba(241, 245, 249, 0.8)", fontSize: "0.95rem", margin: 0 }}>Daily candles — latest trading session</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === "patterns" && (
              <PatternsTab
                items={candleItems}
                uniqueTypes={uniqueCandleTypes}
                setupGroups={patternSetupGroups}
                selectedType={selectedCandleType}
                onSelectType={(t) => {
                  setSelectedCandleType(t);
                  setSelectedPatternForKnowledge(null);
                }}
                selectedPatternInfo={selectedPatternForKnowledge}
                trendFilter={trendFilter}
                setTrendFilter={setTrendFilter}
                timeframe={timeframe}
              />
            )}

            {activeTab === "doji" && (
              <DojiTab
                items={dojiItems}
                uniqueTypes={uniqueDojiTypes}
                setupGroups={dojiSetupGroups}
                selectedType={selectedDojiType}
                onSelectType={(t) => {
                  setSelectedDojiType(t);
                  setSelectedPatternForKnowledge(null);
                }}
                selectedPatternInfo={selectedPatternForKnowledge}
                trendFilter={trendFilter}
                setTrendFilter={setTrendFilter}
                timeframe={timeframe}
              />
            )}

            {activeTab === "structure" && (
              <StructureTab
                items={structureItemsRaw}
                uniqueTypes={uniqueStructureTypes}
                setupGroups={structureSetupGroups}
                selectedType={selectedStructureType}
                onSelectType={setSelectedStructureType}
                selectedPatternInfo={selectedPatternForKnowledge}
                trendFilter={trendFilter}
                setTrendFilter={setTrendFilter}
              />
            )}

            {activeTab === "reversal" && (
              <ReversalTab
                items={reversalItemsRaw}
                uniqueTypes={uniqueReversalTypes}
                setupGroups={reversalSetupGroups}
                selectedType={selectedReversalType}
                onSelectType={setSelectedReversalType}
                selectedPatternInfo={selectedPatternForKnowledge}
                trendFilter={trendFilter}
                setTrendFilter={setTrendFilter}
              />
            )}

            {activeTab === "divergences" && (
              <DivergencesTab
                items={divergencesBase}
                filter={divergenceFilter}
                setFilter={setDivergenceFilter}
                classFilter={divergenceClassFilter}
                setClassFilter={setDivergenceClassFilter}
              />
            )}

            {activeTab === "trend" && <TrendTab timeframe={timeframe} />}
          </>
        )}
      </div>
    </section>
  );
}
